import { defineStore } from 'pinia';
import { navigateTo, useNuxtApp } from '#app'; // Needed for Nuxt integration
import { useAvatar } from '~/composables/useAvatar';
import { resetAllStores } from '~/lib/functions/storeControls';
import { useRouter } from 'vue-router';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: { id: '', avatar_url: '' },
    userProfile: { id: '', avatar_url: '' },
    loading: true,
    isFetching: false,
    isAdmin: false,
  }),
  getters: {
    isLoggedIn: (state) => !!state.user?.id,
  },
  actions: {
    async initAuth() {
      this.loading = true;
      const { $supabase } = useNuxtApp();

      const {
        data: { session },
      } = await $supabase.auth.getSession();

      if (session?.user) {
        this.user = session.user;
        await this.checkAdmin();
        this.loading = false;
        return;
      }

      // Session not ready yet (Google PKCE still exchanging) — listen for it
      const {
        data: { subscription },
      } = $supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (event === 'SIGNED_IN' && newSession?.user) {
          this.user = newSession.user;
          // Keep your custom session key in sync
          localStorage.setItem('session', JSON.stringify(newSession));
          await this.checkAdmin();
          this.loading = false;
          subscription.unsubscribe();
        } else if (event === 'SIGNED_OUT') {
          this.user = { id: '', avatar_url: '' };
          this.isAdmin = false;
          this.loading = false;
          subscription.unsubscribe();
        }
      });

      // Safety net — never leave the user stuck forever
      setTimeout(() => {
        if (this.loading) {
          this.loading = false;
          subscription.unsubscribe();
        }
      }, 5000);
    },
    async checkAdmin() {
      const { $supabase } = useNuxtApp();
      try {
        const { data, error } = await $supabase.functions.invoke('check-admin');
        if (error) throw error;
        this.isAdmin = data?.isAdmin ?? false;
      } catch (error) {
        console.error('Error checking admin status:', error);
        this.isAdmin = false;
      }
    },
    async fetchUserAndProfile() {
      this.isFetching = true;
      const { $supabase } = useNuxtApp(); // Access supabase client
      try {
        // Fetch session from localStorage
        const session = await this.getSessionFromLocalStorage();

        if (!session || !session.user) {
          this.loading = false;
          this.isFetching = false;
          return;
        }

        // Set user in Pinia state
        this.user = session.user;

        // Fetch user profile using the user ID
        const { data: profileData, error: profileError } = await $supabase
          .from('profiles')
          .select('*')
          .eq('id', this.user.id)
          .single();

        if (profileError) {
          this.loading = false;
          this.isFetching = false;
          return;
        }

        // Store user profile data
        this.userProfile = profileData;
      } catch (error) {
        console.error('Unexpected error during fetch:', error);
      } finally {
        this.loading = false;
        this.isFetching = false;
        return this.userProfile;
      }
    },
    async resetUserStore() {
      this.user = { id: '', avatar_url: '' };
      this.userProfile = { id: '', avatar_url: '' };
      this.isAdmin = false;
      this.loading = false;
    },
    async signOutUser() {
      this.loading = true;
      const { $supabase } = useNuxtApp();

      const { error } = await $supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error.message);
        this.loading = false;
        return;
      }

      localStorage.clear();
      this.user = { id: '' };
      this.userProfile = { id: '', avatar_url: '' };
      this.isAdmin = false;
      await resetAllStores();

      // Navigate first, THEN clean up loading state
      await navigateTo('/login');
      this.loading = false;
    },
    async fetchUserProfile(userID, user) {
      const avatar = useAvatar();
      const { $supabase } = useNuxtApp(); // Access supabase client
      const { generateAvatar } = avatar;

      try {
        const { data, error } = await $supabase.functions.invoke(
          'get-user-profile',
          {
            body: JSON.stringify({ user: user }),
          }
        );
        if (error) {
          console.error('Error invoking function:', error);
          this.loading = false;
          return;
        }
        // Check if the response contains the expected profile data
        const profileData = data;
        if (!profileData || !profileData.profile) {
          console.error('Invalid profile data:', profileData);
          this.loading = false;
          return;
        }

        // Store user profile data
        this.userProfile = profileData.profile;
        await generateAvatar(profileData.profile);
        this.loading = false;
      } catch (error) {
        console.error('Unexpected error during fetch:', error);
      } finally {
        this.loading = false;
      }
    },

    // Utility function for session retrieval
    async getSessionFromLocalStorage() {
      const { $supabase } = useNuxtApp();
      try {
        const {
          data: { session },
          error,
        } = await $supabase.auth.getSession();
        if (error) throw error;

        // Keep writing to 'session' key if anything else in your app reads it
        if (session) {
          localStorage.setItem('session', JSON.stringify(session));
        }

        return session;
      } catch (error) {
        console.error('Error fetching session:', error);
        return null;
      }
    },
  },
});
